// src/pages/AccountSettings.js
//
// Pass 44 — UNIVERSAL account settings. Applies to the HUMAN, not a role. Renders
// inside the global shell only (no StudentNav, no student-only sections). Client
// coaching prefs (sports/level/coach) live in the My Coaching workspace; instructor
// and organisation settings live in their own workspaces.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { PRODUCT_NAME } from '../config/product';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { useWorkspace } from '../WorkspaceContext';

const C = { bg: '#FAFAF8', card: '#FFFFFF', card2: '#F5F4F0', border: 'rgba(0,0,0,0.08)', t1: '#1C1B18', t2: '#57564F', t3: '#8A8980', blue: '#2563EB', green: '#16A34A' };
const font = "'DM Sans',system-ui,sans-serif";
const SECTIONS = [
  { key: 'personal', label: 'Personal info' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'privacy', label: 'Privacy' },
  { key: 'security', label: 'Security' },
];
const NOTIF_TOGGLES = [
  ['notifFeedback', 'Feedback ready'], ['notifMessage', 'New messages'], ['notifDrill', 'Drill assignments'],
  ['notifWeekly', 'Weekly summary'], ['notifFeatures', 'Product updates'], ['notifTips', 'Coaching tips'],
];

export default function AccountSettings() {
  const navigate = useNavigate();
  const { userData, currentUser, refreshUserData } = useAuth() || {};
  const { workspaces, currentWorkspace, WORKSPACES } = useWorkspace() || {};
  // Pass 58G2 — a clear return to the CURRENT workspace (Account is cross-workspace, so the workspace nav
  // disappears). Operations routes to the actual selected org, not a generic dead page.
  const ws = (WORKSPACES && WORKSPACES[currentWorkspace]) || null;
  const backOrgId = userData?.orgId || userData?.organizationId || '';
  const backTo = currentWorkspace === 'organization' && backOrgId ? `/org/${backOrgId}` : (ws?.home || '/dashboard');
  const backLabel = ws?.label || 'Workspace';
  const [section, setSection] = useState('personal');
  const [f, setF] = useState({
    name: userData?.name || '', phone: userData?.phone || '', country: userData?.country || '',
    timezone: userData?.timezone || '', language: userData?.language || '', bio: userData?.bio || '',
  });
  const [notif, setNotif] = useState(() => Object.fromEntries(NOTIF_TOGGLES.map(([k]) => [k, userData?.[k] !== false])));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async (payload, label) => {
    if (!currentUser?.uid) return;
    setSaving(true); setMsg('');
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { ...payload, updatedAt: new Date() });
      if (refreshUserData) await refreshUserData();
      setMsg(`${label} saved.`);
    } catch (e) { setMsg(`Could not save: ${e.message}`); }
    setSaving(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: font, color: C.t1 }}>
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '28px 22px 80px' }}>
        <button data-testid="account-back" onClick={() => navigate(backTo)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: C.blue, cursor: 'pointer', fontFamily: font, fontSize: 13.5, fontWeight: 700, padding: 0, marginBottom: 14 }}>← Back to {backLabel}</button>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: '0 0 4px' }}>Account settings</h1>
        <p style={{ color: C.t3, fontSize: 14, margin: '0 0 22px' }}>These settings apply to your {PRODUCT_NAME} account across every workspace.</p>

        <div className="m1" style={{ display: 'grid', gridTemplateColumns: '190px 1fr', gap: 24 }}>
          {/* page-local sidebar (allowed) */}
          <nav>
            {SECTIONS.map((s) => (
              <button key={s.key} onClick={() => setSection(s.key)} style={{ display: 'block', width: '100%', textAlign: 'left', background: section === s.key ? C.card2 : 'none', border: 'none', borderRadius: 9, padding: '10px 12px', cursor: 'pointer', fontFamily: font, fontSize: 14, fontWeight: section === s.key ? 700 : 500, color: C.t1, marginBottom: 2 }}>{s.label}</button>
            ))}
            {(workspaces || []).includes('personal') && (
              <button onClick={() => navigate('/me/coaching-profile')} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderRadius: 9, padding: '10px 12px', cursor: 'pointer', fontFamily: font, fontSize: 13, color: C.blue, marginTop: 8 }}>Coaching profile →</button>
            )}
          </nav>

          <div>
            {msg && <div style={{ background: 'rgba(22,163,74,0.1)', border: `1px solid ${C.green}`, color: C.green, borderRadius: 8, padding: '9px 12px', marginBottom: 14, fontSize: 13 }}>{msg}</div>}

            {section === 'personal' && (
              <Card title="Personal info">
                <Field label="Full name"><input value={f.name} onChange={set('name')} style={inp} /></Field>
                <Field label="Email"><input value={userData?.email || ''} disabled style={{ ...inp, background: C.card2, color: C.t3 }} /></Field>
                <div className="m1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Phone"><input value={f.phone} onChange={set('phone')} style={inp} /></Field>
                  <Field label="Country"><input value={f.country} onChange={set('country')} style={inp} /></Field>
                  <Field label="Timezone"><input value={f.timezone} onChange={set('timezone')} placeholder="Asia/Tokyo" style={inp} /></Field>
                  <Field label="Preferred language"><input value={f.language} onChange={set('language')} placeholder="English" style={inp} /></Field>
                </div>
                <Field label="Bio"><textarea value={f.bio} onChange={set('bio')} style={{ ...inp, minHeight: 64, resize: 'vertical' }} /></Field>
                <SaveBtn saving={saving} onClick={() => save(f, 'Personal info')} />
              </Card>
            )}

            {section === 'notifications' && (
              <Card title="Notifications">
                {NOTIF_TOGGLES.map(([k, label]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontSize: 14 }}>
                    <input type="checkbox" checked={notif[k]} onChange={(e) => setNotif((p) => ({ ...p, [k]: e.target.checked }))} /> {label}
                  </label>
                ))}
                <SaveBtn saving={saving} onClick={() => save(notif, 'Notification preferences')} />
              </Card>
            )}

            {section === 'privacy' && (
              <Card title="Privacy">
                <p style={{ color: C.t2, fontSize: 14, lineHeight: 1.6 }}>Control how your profile, progress and videos are shared. Managed with your organisation where applicable.</p>
              </Card>
            )}

            {section === 'security' && (
              <Card title="Security">
                <p style={{ color: C.t2, fontSize: 14, lineHeight: 1.6, marginBottom: 12 }}>Signed in as <b>{userData?.email}</b>.</p>
                <button onClick={() => navigate('/login')} style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 16px', cursor: 'pointer', fontFamily: font, fontWeight: 600, fontSize: 13 }}>Manage sign-in</button>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const inp = { width: '100%', boxSizing: 'border-box', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 12px', fontSize: 14, fontFamily: font, color: C.t1, marginBottom: 12 };
function Card({ title, children }) { return <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '20px 22px' }}><h2 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 14px' }}>{title}</h2>{children}</div>; }
function Field({ label, children }) { return <label style={{ display: 'block' }}><span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: C.t3, display: 'block', marginBottom: 4 }}>{label}</span>{children}</label>; }
function SaveBtn({ saving, onClick }) { return <button disabled={saving} onClick={onClick} style={{ marginTop: 6, background: C.blue, color: '#fff', border: 'none', borderRadius: 9, padding: '10px 22px', cursor: 'pointer', fontFamily: font, fontWeight: 700, fontSize: 14, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>; }
